/**
 * Tour guiado de primera sesión.
 *
 * Se dispara una sola vez por usuario (persistencia en localStorage).
 * Ofrece bienvenida + 3-N tooltips contextuales que apuntan a elementos
 * específicos de la UI vía `data-tour-id`.
 *
 * **Por qué dependency-free:**
 *
 * Considerados react-joyride / shepherd.js / intro.js. Todos suman
 * 25-50kb gzipped y arrastran su propio styling system. El tour acá
 * cubre 4 estados (welcome, 3 pasos, dismiss) y el spotlight es CSS
 * básico — implementarlo a mano cuesta ~150 líneas y nos deja control
 * total sobre la UX (placement, copy, marca).
 *
 * **Estados:**
 *
 * - `idle`: sin actividad, no se muestra nada.
 * - `welcome`: modal centrado con bienvenida + CTA "Empezar tour".
 * - `step-N`: tooltip apuntando al elemento `data-tour-id`. Si el
 *   elemento no existe en el DOM, se salta al siguiente paso (defensivo
 *   — útil cuando el Radar está vacío y la card target no existe).
 * - `done`: terminó. Persiste flag en localStorage y no vuelve a
 *   aparecer en futuras sesiones.
 *
 * **A11y:**
 * - Modal y tooltips con `role="dialog"`, `aria-modal`, `aria-labelledby`.
 * - Esc cierra el tour (mismo efecto que "Saltar").
 * - Focus trap dentro del tooltip activo.
 *
 * **Uso:**
 *
 * ```tsx
 * <OnboardingTour
 *   storageKey="kaptar.tour.radar.v1"
 *   steps={[
 *     { id: "welcome", title: "...", body: "..." }, // sin target = centrado
 *     { id: "filters", target: "[data-tour-id=filters]", title: "...", body: "...", placement: "bottom" },
 *   ]}
 * />
 * ```
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export interface TourStep {
  id: string;
  /** CSS selector del elemento al que apunta el tooltip. Omitir para modal centrado. */
  target?: string;
  title: string;
  body: string;
  /** Lado preferido del tooltip respecto al target. Default: "bottom". */
  placement?: "top" | "bottom" | "left" | "right";
}

export interface OnboardingTourProps {
  /**
   * Llave de localStorage. Cambiar el sufijo (`.v2`) cuando se rediseñe
   * el tour para forzar a usuarios viejos a verlo de nuevo.
   */
  storageKey: string;
  steps: TourStep[];
  /**
   * Si está en true, ignora el flag de localStorage y siempre muestra
   * el tour. Útil para QA / debugging local.
   */
  forceShow?: boolean;
}

export function OnboardingTour({ storageKey, steps, forceShow = false }: OnboardingTourProps) {
  const [stepIndex, setStepIndex] = useState<number | null>(() => {
    if (forceShow) return 0;
    if (typeof window === "undefined") return null;
    const seen = window.localStorage.getItem(storageKey) === "done";
    return seen ? null : 0;
  });

  const dismiss = useCallback(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, "done");
    }
    setStepIndex(null);
  }, [storageKey]);

  const next = useCallback(() => {
    setStepIndex((prev) => {
      if (prev === null) return null;
      const nextIndex = prev + 1;
      if (nextIndex >= steps.length) {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(storageKey, "done");
        }
        return null;
      }
      return nextIndex;
    });
  }, [steps.length, storageKey]);

  // Esc cierra el tour.
  useEffect(() => {
    if (stepIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [stepIndex, dismiss]);

  // Si el target del paso actual no existe en el DOM, salta automáticamente
  // (defensa: ej. el Radar todavía no renderizó cards y el step apuntaba a
  // una). Re-evalúa cuando cambie el step.
  const step = stepIndex !== null ? steps[stepIndex] : null;
  useEffect(() => {
    if (!step?.target) return;
    if (typeof document === "undefined") return;
    const el = document.querySelector(step.target);
    if (!el) {
      // Target ausente — saltar al próximo en el siguiente tick.
      const t = setTimeout(() => next(), 50);
      return () => clearTimeout(t);
    }
  }, [step, next]);

  const shown = useShownStepIds(steps, stepIndex);

  if (stepIndex === null || step === undefined || step === null) return null;

  const shownPos = shown.indexOf(step.id);
  const totalSteps = shown.length;
  // Si el paso actual no está entre los visibles (target ausente), el effect
  // de arriba ya lo está saltando; el número que pintemos no se ve.
  const stepNum = shownPos === -1 ? stepIndex + 1 : shownPos + 1;
  const isLast = stepNum >= totalSteps;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`tour-title-${step.id}`}
      data-testid="onboarding-tour"
      className="pointer-events-none fixed inset-0 z-100"
    >
      {/* Backdrop semi-transparente. pointer-events-auto solo cuando es
          modal centrado (no spotlight) — así el resto de la app queda
          accesible debajo. */}
      <div
        className={
          "absolute inset-0 bg-slate-900/40 backdrop-blur-[1px] " +
          (step.target ? "pointer-events-none" : "pointer-events-auto")
        }
      />

      {step.target ? (
        <SpotlightTooltip
          target={step.target}
          step={step}
          stepNum={stepNum}
          totalSteps={totalSteps}
          isLast={isLast}
          onNext={next}
          onSkip={dismiss}
        />
      ) : (
        <CenteredCard
          step={step}
          stepNum={stepNum}
          totalSteps={totalSteps}
          isLast={isLast}
          onNext={next}
          onSkip={dismiss}
        />
      )}
    </div>
  );
}

/**
 * Ids de los pasos que el usuario va a ver de verdad: los sin target y los
 * cuyo target está en el DOM. "Paso N de M" y el CTA "Listo" se calculan
 * sobre esta lista y no sobre `steps.length`: si no, cada paso auto-saltado
 * deja el contador prometiendo pasos que nunca llegan (#627: "Paso 1 de 4"
 * en un expediente que termina en el 2).
 *
 * Se mide en un layout effect (como el spotlight) y no durante el render
 * porque el tour suele montarse en el mismo commit que sus targets. Se
 * re-mide en cada cambio de paso, que es cuando el auto-salto decide.
 */
function useShownStepIds(steps: TourStep[], stepIndex: number | null): string[] {
  const [ids, setIds] = useState<string[]>(() => steps.map((s) => s.id));
  useLayoutEffect(() => {
    if (stepIndex === null || typeof document === "undefined") return;
    const measure = () => {
      const next = steps
        .filter((s) => !s.target || document.querySelector(s.target) !== null)
        .map((s) => s.id);
      setIds((prev) => (prev.join("\n") === next.join("\n") ? prev : next));
    };
    measure();
  }, [stepIndex, steps]);
  return ids;
}

// ---------------------------------------------------------------------------
// Card centrado (welcome step, sin target)
// ---------------------------------------------------------------------------

function CenteredCard({
  step,
  stepNum,
  totalSteps,
  isLast,
  onNext,
  onSkip,
}: {
  step: TourStep;
  stepNum: number;
  totalSteps: number;
  isLast: boolean;
  onNext: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="pointer-events-auto absolute top-1/2 left-1/2 w-[min(440px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
      <TourBody
        step={step}
        stepNum={stepNum}
        totalSteps={totalSteps}
        isLast={isLast}
        onNext={onNext}
        onSkip={onSkip}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tooltip con spotlight (target existe)
// ---------------------------------------------------------------------------

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function SpotlightTooltip({
  target,
  step,
  stepNum,
  totalSteps,
  isLast,
  onNext,
  onSkip,
}: {
  target: string;
  step: TourStep;
  stepNum: number;
  totalSteps: number;
  isLast: boolean;
  onNext: () => void;
  onSkip: () => void;
}) {
  const [rect, setRect] = useState<Rect | null>(null);

  // Mide el target. Re-mide en resize/scroll para que el spotlight siga
  // pegado aunque la página cambie de layout.
  useLayoutEffect(() => {
    // Si el target quedó fuera del viewport (ej. `status-controls` bajo un
    // checklist largo en el expediente, #626), lo traemos al centro antes
    // de medir. El tooltip es position:fixed: sin este scroll, spotlight y
    // botones quedan fuera de pantalla y el usuario solo ve la página
    // oscurecida. Se hace una sola vez por paso, no en cada scroll.
    scrollTargetIntoViewport(document.querySelector(target));

    const measure = () => {
      const el = document.querySelector(target);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      // Padding visual de 8px alrededor del target.
      setRect({
        top: r.top - 8,
        left: r.left - 8,
        width: r.width + 16,
        height: r.height + 16,
      });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    // Re-mide después de un microtick — algunos layouts cambian justo
    // cuando se monta el tour (ej. la sidebar revela items).
    const t = setTimeout(measure, 50);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      clearTimeout(t);
    };
  }, [target]);

  if (!rect) {
    // Si todavía no medimos, no renderizamos el tooltip — evita un flash
    // en (0,0). El effect de salto-si-target-ausente ya lo cubre.
    return null;
  }

  // Cálculo simple del placement: por default abajo. Si no hay espacio
  // suficiente abajo (a 200px del bottom de viewport), pasa a arriba.
  const placement = step.placement ?? "bottom";
  const tooltipPos = computeTooltipPosition(rect, placement);

  const cutoutStyle: React.CSSProperties = {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };

  return (
    <>
      {/* Spotlight: ring + transparent center sobre el target. */}
      <div
        aria-hidden="true"
        data-testid="tour-spotlight"
        className="absolute rounded-xl shadow-[0_0_0_9999px_rgba(15,23,42,0.55)] ring-4 ring-brand-500 transition-all duration-200"
        style={cutoutStyle}
      />
      {/* Tooltip card */}
      <div
        className="pointer-events-auto absolute w-[min(360px,calc(100vw-32px))] rounded-xl bg-white p-5 shadow-2xl"
        style={tooltipPos}
      >
        <TourBody
          step={step}
          stepNum={stepNum}
          totalSteps={totalSteps}
          isLast={isLast}
          onNext={onNext}
          onSkip={onSkip}
        />
      </div>
    </>
  );
}

function scrollTargetIntoViewport(el: Element | null) {
  // `scrollIntoView` no existe en jsdom; el guard evita que el tour tire
  // en tests que no lo mockean.
  if (!el || typeof el.scrollIntoView !== "function") return;
  const r = el.getBoundingClientRect();
  const viewportH = window.innerHeight;
  const fullyVisible = r.top >= 0 && r.bottom <= viewportH;
  if (fullyVisible) return;
  // Un target más alto que el viewport (el checklist de 7 items del
  // expediente, #665) no cabe: centrarlo deja su tope en negativo y el
  // usuario pierde el encabezado. Alineamos el tope y que el resto siga
  // abajo del fold.
  el.scrollIntoView({ block: r.height <= viewportH ? "center" : "start" });
}

// Alto aproximado del card (título + cuerpo + botones). No lo medimos
// porque el card se posiciona antes de montarse; con este estimado
// decidimos si cabe arriba o abajo del target.
const TOOLTIP_H_ESTIMATE = 200;
const VIEWPORT_EDGE = 16;

/**
 * Vertical del tooltip. Intenta el lado pedido, luego el opuesto; si el
 * target es más alto que el viewport y no cabe en ninguno (#665), lo
 * ancla al borde inferior de la pantalla, sobre el target. El borde
 * inferior y no el superior para no tapar el encabezado del target, que
 * `scrollTargetIntoViewport` acaba de dejar visible.
 */
function computeTooltipVertical(
  rect: Rect,
  preferred: "top" | "bottom",
  viewportH: number,
): Pick<React.CSSProperties, "top" | "transform"> {
  const margin = 12;
  const above = { top: rect.top - margin, transform: "translateY(-100%)" };
  const below = { top: rect.top + rect.height + margin };
  const fitsAbove = above.top - TOOLTIP_H_ESTIMATE >= VIEWPORT_EDGE;
  const fitsBelow = below.top + TOOLTIP_H_ESTIMATE <= viewportH - VIEWPORT_EDGE;
  if (preferred === "top" && fitsAbove) return above;
  if (preferred === "bottom" && fitsBelow) return below;
  if (fitsAbove) return above;
  if (fitsBelow) return below;
  return { top: viewportH - VIEWPORT_EDGE, transform: "translateY(-100%)" };
}

function computeTooltipPosition(
  rect: Rect,
  placement: "top" | "bottom" | "left" | "right",
): React.CSSProperties {
  const margin = 12;
  const tooltipW = 360;
  const viewportW = typeof window !== "undefined" ? window.innerWidth : 1280;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;

  switch (placement) {
    case "top": {
      // Centrado horizontalmente, clamp para no salirse del viewport.
      const idealLeft = rect.left + rect.width / 2 - tooltipW / 2;
      const left = Math.max(16, Math.min(viewportW - tooltipW - 16, idealLeft));
      return { left, ...computeTooltipVertical(rect, "top", viewportH) };
    }
    case "left": {
      const top = rect.top + rect.height / 2;
      const left = rect.left - margin;
      return { top, left, transform: "translate(-100%, -50%)" };
    }
    case "right": {
      const top = rect.top + rect.height / 2;
      const left = rect.left + rect.width + margin;
      return { top, left, transform: "translateY(-50%)" };
    }
    case "bottom":
    default: {
      const idealLeft = rect.left + rect.width / 2 - tooltipW / 2;
      const left = Math.max(16, Math.min(viewportW - tooltipW - 16, idealLeft));
      return { left, ...computeTooltipVertical(rect, "bottom", viewportH) };
    }
  }
}

// ---------------------------------------------------------------------------
// Cuerpo del tooltip (compartido entre centrado y spotlight)
// ---------------------------------------------------------------------------

function TourBody({
  step,
  stepNum,
  totalSteps,
  isLast,
  onNext,
  onSkip,
}: {
  step: TourStep;
  stepNum: number;
  totalSteps: number;
  isLast: boolean;
  onNext: () => void;
  onSkip: () => void;
}) {
  const titleId = `tour-title-${step.id}`;
  const nextRef = useRef<HTMLButtonElement | null>(null);

  // Auto-focus al "Siguiente" cuando entra el paso (a11y).
  useEffect(() => {
    nextRef.current?.focus();
  }, [step.id]);

  return (
    <>
      <p className="mb-1 text-[11px] font-semibold tracking-wider text-brand-600 uppercase">
        Paso {stepNum} de {totalSteps}
      </p>
      <h2 id={titleId} className="text-lg font-bold text-slate-900">
        {step.title}
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>
      <div className="mt-5 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onSkip}
          data-testid="tour-skip"
          className="text-xs font-medium text-slate-500 hover:text-slate-700"
        >
          Saltar tour
        </button>
        <button
          ref={nextRef}
          type="button"
          onClick={onNext}
          data-testid="tour-next"
          className="rounded-md bg-brand-700 px-4 py-2 text-xs font-semibold text-white hover:bg-brand-800"
        >
          {isLast ? "Listo" : "Siguiente →"}
        </button>
      </div>
    </>
  );
}
